<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>AI credits</h1>
        <p>
          GNey, AI form fill, generated documents and automations that think all draw on one
          workspace balance.
        </p>
      </div>
    </header>

    <div class="fts-settings-loading" v-if="loading">
      <fts-spinner :size="16" />
      <span>Loading&hellip;</span>
    </div>

    <section class="fts-settings-card" v-else-if="unavailable">
      <h2>{{ unavailable.title }}</h2>
      <p class="fts-settings-card-sub">{{ unavailable.body }}</p>
    </section>

    <template v-else>
      <!-- Balance, and how long it lasts. A pay-as-you-go balance with no burn rate tells an
           admin nothing until the day it reaches zero. -->
      <section class="fts-settings-card">
        <div class="fts-settings-credits">
          <div class="fts-settings-credits-balance">
            <div class="fts-settings-eyebrow">Balance</div>
            <div class="fts-settings-credits-big">{{ balanceLabel }}</div>
            <div v-if="runway" class="fts-settings-banner" :class="runway.tone">
              <font-awesome-icon :icon="['fas', runway.icon]" />
              <span>{{ runway.text }}</span>
            </div>
            <div class="fts-settings-credits-facts">
              <span v-for="fact in facts" :key="fact">{{ fact }}</span>
            </div>
          </div>

          <div class="fts-settings-credits-chart">
            <div class="fts-settings-credits-chart-head">
              <span>{{ usageTotalLabel }}</span>
              <span class="fts-settings-credits-legend">
                <span v-for="c in categories" :key="c.key">
                  <i :style="{ background: c.color }"></i>{{ c.label }}
                </span>
              </span>
            </div>
            <div class="fts-settings-credits-bars" role="img" :aria-label="usageTotalLabel">
              <span
                v-for="(day, i) in days"
                :key="i"
                class="fts-settings-credits-day"
                :title="day.title"
              >
                <i
                  v-for="seg in day.segments"
                  :key="seg.key"
                  :style="{ height: seg.height, background: seg.color }"
                ></i>
              </span>
            </div>
            <div class="fts-settings-credits-axis" v-if="days.length">
              <span>{{ days[0].label }}</span>
              <span>{{ days[days.length - 1].label }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="fts-settings-card" v-if="packs.length">
        <div>
          <h2>Top up</h2>
          <p class="fts-settings-card-sub">
            One-off packs. Bigger packs cost less per credit, and credits never expire.
          </p>
        </div>

        <div class="fts-settings-packs" role="radiogroup" aria-label="Credit pack">
          <button
            type="button"
            v-for="pack in packs"
            :key="pack.packKey"
            role="radio"
            :aria-checked="String(pack.packKey === chosenPack)"
            class="fts-settings-pack"
            :class="{ 'is-on': pack.packKey === chosenPack }"
            @click="chosenPack = pack.packKey"
          >
            <span class="fts-settings-pack-credits">
              {{ pack.creditsLabel }} <small>credits</small>
            </span>
            <span class="fts-settings-pack-price">{{ pack.priceLabel }}</span>
            <span class="fts-settings-pack-note">{{ pack.rate }}</span>
            <span class="fts-settings-pack-note" v-if="pack.lasts">{{ pack.lasts }}</span>
          </button>
        </div>

        <div class="fts-settings-pack-buy">
          <span class="fts-settings-note">{{ afterLine }}</span>
          <button type="button" class="fts-settings-btn is-primary" :disabled="busy" @click="buy">
            {{ buyLabel }}
          </button>
        </div>
      </section>

      <section class="fts-settings-card" v-else-if="isManual">
        <h2>Credits are handled by {{ billedByLabel }}</h2>
        <p class="fts-settings-card-sub">
          Top-ups and plan changes go through them. Vouchers still work here.
        </p>
      </section>

      <section class="fts-settings-card">
        <div>
          <h2>Redeem a voucher</h2>
          <p class="fts-settings-card-sub">A code adds credits that never expire.</p>
        </div>
        <div class="fts-settings-voucher">
          <input
            v-model="voucher"
            type="text"
            maxlength="24"
            aria-label="Voucher code"
            placeholder="Have a code?"
            @keyup.enter="redeem"
          />
          <button type="button" class="fts-settings-btn" :disabled="busy || !voucher" @click="redeem">
            Redeem
          </button>
        </div>
      </section>

      <section class="fts-settings-card">
        <div>
          <h2>Credit history</h2>
          <p class="fts-settings-card-sub">
            Top-ups, vouchers and grants. Day-to-day use is charted above rather than listed here.
          </p>
        </div>

        <div class="fts-settings-note" v-if="!transactions.length">No credit activity yet.</div>
        <table class="fts-settings-table" v-else>
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Entry</th>
              <th scope="col">Detail</th>
              <th scope="col" class="is-right">Credits</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tx in transactions" :key="tx.id">
              <td>{{ tx.when }}</td>
              <td>{{ tx.label }}</td>
              <td class="is-muted">{{ tx.detail }}</td>
              <td class="is-right" :class="{ 'is-neg': tx.negative }">{{ tx.amount }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>

<script>
/**
 * Settings › AI credits (P1).
 *
 * Built entirely on endpoints that already exist — summary, usage/daily, transactions,
 * checkout/credits, redeem — so it needs no backend of its own. The runway is computed here
 * from the same 30 days the chart draws, rather than asking the server for a number it does
 * not keep.
 *
 * Deliberately NOT drawn: credits by person and by module. Those need the breakdown endpoint
 * (proposal §4 #3); deriving them from a daily total would be a guess dressed as a fact.
 */
import { commonStore } from "../../store/commonStore";
import { formatMoney } from "../../commons/billing";
import FTSSpinner from "../../elements/FTSSpinner";

const CATEGORIES = [
  { key: "chat", label: "Chat", color: "#2b9ec6" },
  { key: "design", label: "Design", color: "#4f46e5" },
  { key: "automation", label: "Automation", color: "#7d9f27" },
  { key: "routines", label: "Routines", color: "#a3a3a3" },
];

export default {
  name: "SettingsCredits",
  components: { "fts-spinner": FTSSpinner },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      busy: false,
      unavailable: null,
      summary: null,
      usage: [],
      transactions: [],
      chosenPack: null,
      voucher: "",
      categories: CATEGORIES,
    };
  },
  computed: {
    isManual() {
      return this.summary && this.summary.source === "manual";
    },
    billedByLabel() {
      return (this.summary && this.summary.billedBy) || "your vendor";
    },
    balance() {
      const c = (this.summary && this.summary.credits) || {};
      return (c.plan || 0) + (c.purchased || 0);
    },
    balanceLabel() {
      if (this.summary && this.summary.uncapped) return "Unlimited";
      return this.number(this.balance);
    },
    dailyAverage() {
      if (!this.usage.length) return 0;
      const total = this.usage.reduce((sum, d) => sum + (Number(d.credits) || 0), 0);
      return total / this.usage.length;
    },
    runway() {
      if (!this.summary || this.summary.uncapped) return null;
      if (this.balance <= 0) {
        return {
          tone: "is-warn",
          icon: "exclamation-triangle",
          text: "No credits left. GNey and AI steps are paused; everything else keeps working.",
        };
      }
      if (this.dailyAverage <= 0) return null;
      const days = Math.floor(this.balance / this.dailyAverage);
      return {
        tone: days <= 14 ? "is-warn" : "",
        icon: days <= 14 ? "exclamation-triangle" : "check",
        text: "About " + this.number(days) + " days left at this pace.",
      };
    },
    facts() {
      const out = [];
      if (this.dailyAverage > 0) {
        out.push(this.number(this.dailyAverage) + " credits a day on average");
      }
      out.push("Credits never expire");
      const c = (this.summary && this.summary.credits) || {};
      if (c.includedMonthly > 0) {
        out.push(this.number(c.includedMonthly) + " included each month");
      }
      return out;
    },
    usageTotal() {
      return this.usage.reduce((sum, d) => sum + (Number(d.credits) || 0), 0);
    },
    usageTotalLabel() {
      return this.usage.length
        ? this.number(this.usageTotal) + " credits in the last " + this.usage.length + " days"
        : "No AI usage in this period";
    },
    days() {
      const max = this.usage.reduce((m, d) => Math.max(m, Number(d.credits) || 0), 0);
      if (!max) return [];
      return this.usage.map(d => {
        const segments = CATEGORIES.map(c => {
          const value = (d.byCategory && d.byCategory[c.key]) || 0;
          return { key: c.key, color: c.color, height: (value / max) * 100 + "%", value };
        }).filter(s => s.value > 0);
        const parts = CATEGORIES
          .map(c => ({ label: c.label, value: (d.byCategory && d.byCategory[c.key]) || 0 }))
          .filter(x => x.value > 0)
          .map(x => x.label + " " + this.number(x.value));
        return {
          segments,
          label: this.shortDate(d.date),
          title: this.shortDate(d.date) + " — " + this.number(d.credits) + " credits"
            + (parts.length ? " (" + parts.join(", ") + ")" : ""),
        };
      });
    },
    packs() {
      if (this.isManual) return [];
      const packs = ((this.summary && this.summary.packs) || []).filter(p => p.hasPrice);
      return packs.map(p => {
        const perCredit = p.credits > 0 ? p.price / p.credits : 0;
        return {
          packKey: p.packKey,
          credits: p.credits,
          creditsLabel: this.number(p.credits),
          priceLabel: this.money(p.price),
          rate: perCredit ? this.money(perCredit, 4) + " a credit" : "",
          lasts: this.dailyAverage > 0
            ? "About " + this.number(Math.round(p.credits / this.dailyAverage)) + " days at your pace"
            : "",
        };
      });
    },
    chosen() {
      return this.packs.find(p => p.packKey === this.chosenPack) || this.packs[0] || null;
    },
    buyLabel() {
      return this.chosen ? "Pay " + this.chosen.priceLabel : "Top up";
    },
    afterLine() {
      if (!this.chosen) return "";
      const after = this.balance + this.chosen.credits;
      const days = this.dailyAverage > 0
        ? ", about " + this.number(Math.round(after / this.dailyAverage)) + " days at your pace"
        : "";
      return "Balance after: " + this.number(after) + " credits" + days + ". Excludes tax.";
    },
  },
  methods: {
    api() {
      return commonStore.state.client;
    },
    number(value) {
      const n = Number(value || 0);
      return Math.abs(n) >= 100
        ? Math.round(n).toLocaleString("en-US")
        : (Math.round(n * 10) / 10).toLocaleString("en-US");
    },
    money(value, digits) {
      const currency = (this.summary && this.summary.currency) || "usd";
      if (digits) {
        const symbols = { usd: "US$", sgd: "S$", myr: "RM" };
        const symbol = symbols[String(currency).toLowerCase()] || "";
        return symbol + Number(value).toFixed(digits);
      }
      return formatMoney(value, currency);
    },
    shortDate(value) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    },
    fmtDate(value) {
      const d = new Date(value);
      return isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    },
    label(tx) {
      const ref = tx.reference || "";
      if (tx.kind === "purchase") return "Credit pack";
      if (tx.kind === "adjust") return "Manual adjustment";
      if (tx.kind === "refund") return "Refund";
      if (tx.kind === "expire") return "Expired credits";
      if (ref === "welcome") return "Welcome credits";
      if (ref.indexOf("voucher:") === 0) return "Voucher";
      return "Grant";
    },
    async load() {
      this.loading = true;
      this.unavailable = null;
      try {
        const res = await this.api().get("/api/billing/summary", {
          params: { workspaceId: this.workspaceId },
        });
        this.summary = res.data;
        const packs = ((this.summary && this.summary.packs) || []).filter(p => p.hasPrice);
        if (packs.length) this.chosenPack = packs[Math.min(1, packs.length - 1)].packKey;
      } catch (err) {
        const status = err && err.response && err.response.status;
        this.unavailable = status === 403
          ? { title: "Only a workspace admin can see AI credits", body: "Ask a workspace admin to top up." }
          : status === 404
            ? { title: "AI credits are arranged with Inistate", body: "Credits are not sold online in this environment." }
            : { title: "Credit information is not available right now", body: "Please try again in a moment." };
        this.loading = false;
        return;
      }
      await Promise.all([this.loadUsage(), this.loadTransactions()]);
      this.loading = false;
    },
    async loadUsage() {
      try {
        const res = await this.api().get("/api/billing/usage/daily", {
          params: { workspaceId: this.workspaceId, days: 30 },
        });
        this.usage = (res.data && res.data.days) || [];
      } catch (err) {
        this.usage = [];
      }
    },
    async loadTransactions() {
      try {
        const res = await this.api().get("/api/billing/transactions", {
          params: { workspaceId: this.workspaceId, page: 0, take: 20 },
        });
        const items = (res.data && res.data.items) || [];
        this.transactions = items.map(tx => ({
          id: tx.id,
          when: this.fmtDate(tx.timestamp),
          label: this.label(tx),
          detail: tx.reference || "",
          negative: Number(tx.delta) < 0,
          amount: (Number(tx.delta) > 0 ? "+" : "−") + this.number(Math.abs(Number(tx.delta))),
        }));
      } catch (err) {
        this.transactions = [];
      }
    },
    async buy() {
      if (!this.chosen || this.busy) return;
      this.busy = true;
      try {
        const res = await this.api().post("/api/billing/checkout/credits", {
          WorkspaceId: this.workspaceId,
          PackKey: this.chosen.packKey,
        });
        const url = res && res.data && res.data.url;
        if (url) {
          window.location.href = url;
        } else {
          this.$toasted.global.error({}, "Checkout did not return a link.");
        }
      } catch (err) {
        this.$toasted.global.error(err, this.message(err, "That purchase could not be started."));
      } finally {
        this.busy = false;
      }
    },
    async redeem() {
      if (!this.voucher || this.busy) return;
      this.busy = true;
      try {
        const res = await this.api().post("/api/billing/redeem", {
          WorkspaceId: this.workspaceId,
          Code: this.voucher,
        });
        const credits = res && res.data && res.data.credits;
        this.voucher = "";
        this.$toasted.success(this.number(credits) + " credits added");
        await this.load();
      } catch (err) {
        this.$toasted.global.error(err, this.message(err, "That code could not be redeemed."));
      } finally {
        this.busy = false;
      }
    },
    message(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && (data.message || data.error)) || fallback;
    },
  },
  watch: {
    workspaceId() {
      this.load();
    },
  },
  created() {
    this.load();
  },
};
</script>
